import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type PlanOptionsResult,
  type PreviewPlanChangeResult,
} from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Changing plan (F-142).
 *
 * The downgrade warning is the feature. It names every feature that locks and
 * the number of records behind each one, because "some features may be
 * affected" is a sentence people click past and "4,182 products become
 * unreachable" is a decision.
 *
 * And it says what does NOT happen: nothing is deleted. "You will lose access
 * to 4,182 products" and "we will delete 4,182 products" are different
 * sentences, and somebody deciding needs to know which one this is.
 */
export function PlanChange(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { refresh } = useSession();
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState<PreviewPlanChangeResult | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const options = useQuery<PlanOptionsResult, ApiError>({
    queryKey: ['plan-options'],
    queryFn: () => api.planOptions({}),
  });

  const review = useMutation<PreviewPlanChangeResult, ApiError, string>({
    mutationFn: (plan) => api.previewPlanChange({ body: { plan } }),
    onSuccess: (result) => {
      setPreview(result);
      setDone(null);
    },
  });

  const confirm = useMutation({
    mutationFn: () =>
      api.changePlan({
        body: {
          plan: preview!.to,
          // The count they were SHOWN. The server refuses if it has moved
          // since, rather than applying against a warning that is out of date.
          ...(preview!.direction === 'downgrade'
            ? { acknowledgeLosing: preview!.losing.length }
            : {}),
        },
      }),
    onSuccess: async (result) => {
      setDone(t('plan.changed', { values: { plan: result.to } }));
      setPreview(null);
      refresh();
      await queryClient.invalidateQueries({ queryKey: ['plan-options'] });
    },
  });

  if (options.isPending) return <Skeleton className="h-block-sm w-full" />;
  // Not a skeleton: one rendered on error never resolves, and the screen waits
  // for it forever.
  if (options.isError) return <></>;

  return (
    <Panel data-region="plan-change">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('plan.change')}</SectionLabel>

        <div className="flex flex-wrap items-center gap-2">
          {/* Labelled, not bare. A select whose only context is the button
              beside it is a control a screen reader announces as "combo box"
              and nothing else. */}
          <label className="flex items-center gap-2 text-dense text-ink-600" htmlFor="plan-target">
            {t('plan.target')}
          </label>
          <select
            id="plan-target"
            data-plan-target
            value={target}
            onChange={(event) => {
              setTarget(event.currentTarget.value);
              setPreview(null);
            }}
            className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
          >
            <option value="">—</option>
            {options.data.options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
                {option.current ? ' ✓' : ''}
              </option>
            ))}
          </select>
          <Button
            intent="secondary"
            size="small"
            data-plan-review
            busy={review.isPending}
            disabled={target === ''}
            onClick={() => review.mutate(target)}
          >
            {t('plan.review')}
          </Button>
        </div>

        {review.isError ? (
          <p data-plan-refusal className="text-dense text-danger-text">
            {errorMessage(review.error)}
          </p>
        ) : null}

        {preview ? (
          <div data-plan-preview data-plan-direction={preview.direction} className="flex flex-col gap-2">
            {preview.direction === 'same' ? (
              <p className="text-dense text-ink-meta">{t('plan.same')}</p>
            ) : preview.direction === 'upgrade' ? (
              <p data-plan-gaining className="text-dense text-ink">
                {t('plan.upgrade.gains', {
                  values: { count: formatCount(preview.gaining.length, format) },
                })}
              </p>
            ) : (
              <div className="flex flex-col gap-2 rounded-control bg-warning-tint p-2">
                <p data-plan-losing className="text-dense text-ink">
                  {t('plan.downgrade.warns', {
                    values: { count: formatCount(preview.losing.length, format) },
                  })}
                </p>
                {/* Each feature by name, with the records behind it. The
                    number IS the warning. */}
                <ul className="flex flex-col gap-0.5">
                  {preview.losing.map((feature) => (
                    <li
                      key={feature.key}
                      data-plan-lost={feature.key}
                      className="flex flex-wrap items-baseline gap-2 text-meta text-ink-600"
                    >
                      <span>{feature.label}</span>
                      {feature.records === null ? (
                        <span className="text-ink-meta">{t('plan.downgrade.noRecords')}</span>
                      ) : (
                        <Num data-plan-records={String(feature.records)} className="text-ink">
                          {t('plan.downgrade.records', {
                            values: { count: formatCount(feature.records, format) },
                          })}
                        </Num>
                      )}
                    </li>
                  ))}
                </ul>
                {/* What does NOT happen, said plainly. */}
                <p data-plan-kept className="text-meta text-ink-meta">
                  {t('plan.downgrade.kept')}
                </p>
              </div>
            )}

            {preview.direction !== 'same' ? (
              <div>
                <Button
                  intent="primary"
                  size="small"
                  data-plan-confirm
                  busy={confirm.isPending}
                  onClick={() => confirm.mutate()}
                >
                  {t('plan.confirm')}
                </Button>
              </div>
            ) : null}
            {confirm.isError ? (
              <p data-plan-confirm-refusal className="text-dense text-danger-text">
                {errorMessage(confirm.error)}
              </p>
            ) : null}
          </div>
        ) : null}

        {done ? (
          <p data-plan-done className="text-dense text-ink">
            {done}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
